import { GetServerSideProps } from 'next'
import Link from 'next/link'

import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import Panel from '@/components/Panel/Panel'
import SimpleHeader from '@/components/SimpleHeader/SimpleHeader'

type Props = {
  initCountry?: string
}

const AboutUs = () => {
  return (
    <Main>
      <Container small>
        <ExplicitHead
          description='About us, the team behind EDHREC'
          keywords='EDHREC,about,about us'
          title='About Us | EDHREC'
        />
        <SimpleHeader>About Us</SimpleHeader>
        <div className='d-grid gap-3'>
          <Panel>
            <p>
              EDHREC was founded in 2015 as a data-driven Magic: The Gathering resource for the{' '}
              <OutboundLink url='https://mtgcommander.net' utm_medium='about_us'>
                Commander
              </OutboundLink>{' '}
              format (also known as EDH). We provide data on popular archetypes, deck staples, and high synergy combos
              to help you build your next deck. We offer direct feedback in the form of{' '}
              <Link href='/recs' target='_blank'>
                Deck Recommendations
              </Link>
              , and Filters to help power users hone in on the perfect 99.
            </p>
            <p>
              We post daily articles on EDHREC and{' '}
              <OutboundLink url='https://commandersherald.com' utm_medium='about_us'>
                Commander&apos;s Herald,
              </OutboundLink>{' '}
              and are home to the{' '}
              <OutboundLink url='https://www.youtube.com/channel/UCApiDwDpn_JpzRGLZKrdr1g' utm_medium='about_us'>
                EDHRECast
              </OutboundLink>
              , a weekly podcast dedicated to providing additional context to the site&apos;s data and card
              recommendations.
            </p>
            <p>
              Recently the team has expanded its efforts to other games including{' '}
              <OutboundLink url='https://raidfeats.gg' utm_medium='about_us'>
                World of Warcraft
              </OutboundLink>
              {', and '}
              <OutboundLink url='https://fabrec.gg' utm_medium='about_us'>
                Flesh and Blood
              </OutboundLink>
              .
            </p>
          </Panel>
          <Panel>
            <h3>Our Data Partners</h3>
            <p className='mb-1'>EDHREC pulls decklists from:</p>
            <ul>
              <li>
                <OutboundLink url='https://www.archidekt.com' utm_medium='about_us'>
                  Archidekt
                </OutboundLink>
              </li>
              <li>
                <OutboundLink url='https://www.moxfield.com/' utm_medium='about_us'>
                  Moxfield
                </OutboundLink>
              </li>
            </ul>
            <p className='mb-1'>EDHREC pulls card data from:</p>
            <ul>
              <li>
                <OutboundLink url='https://www.commanderspellbook.com/' utm_medium='about_us'>
                  Commander Spellbook
                </OutboundLink>
              </li>
              <li>
                <OutboundLink url='https://www.scryfall.com/' utm_medium='about_us'>
                  Scryfall
                </OutboundLink>
              </li>
            </ul>
          </Panel>
        </div>
      </Container>
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  const props: Props = {}
  if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
  return { props }
}

export default AboutUs
